import React, { useState, useEffect } from 'react';
import { transcriptApi } from '../../../api/transcriptApi';
import styles from './styles.module.scss';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

/**
 * SessionQueueStatus Component
 * 
 * Displays inline queue status for transcript generation within session cards.
 * Shows: Session Status, Queue Position, Estimated Wait, Task Status, Started At
 * Auto-refreshes every 15s if status is PROCESSING
 */
const SessionQueueStatus = ({ session, compact = false }) => {
  const [queueStatus, setQueueStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(null);

  // Auto-refresh if processing
  useEffect(() => {
    if (session?.status === 'PROCESSING') {
      // Initial fetch
      fetchQueueStatus();
      
      // Auto-refresh every 15s
      const interval = setInterval(() => {
        fetchQueueStatus();
      }, 15000);
      
      return () => clearInterval(interval);
    }
  }, [session?.status, session?.id]);

  // Calculate elapsed time for PROCESSING status
  useEffect(() => {
    if (session?.status !== 'PROCESSING' || !session?.processing_started_at) {
      setElapsedTime(null);
      return;
    }

    const updateElapsed = () => {
      const now = new Date();
      const start = new Date(session.processing_started_at);
      const seconds = Math.floor((now - start) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes === 0) {
        setElapsedTime(`${remainingSeconds}sec`);
      } else if (remainingSeconds === 0) {
        setElapsedTime(`${minutes}min`);
      } else {
        setElapsedTime(`${minutes}min i ${remainingSeconds}sec`);
      }
    };

    // Update immediately
    updateElapsed();

    // Update every second
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [session?.status, session?.processing_started_at]);

  const fetchQueueStatus = async () => {
    if (!session?.id) return;
    
    try {
      setLoading(true);
      const data = await transcriptApi.getSessionQueueStatus(session.id);
      setQueueStatus(data);
    } catch (error) {
      // Silently ignore 404 errors (session was deleted or doesn't exist)
      if (error?.response?.status !== 404) {
        console.error('Failed to fetch queue status:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'CREATED':
        return styles.statusCreated;
      case 'PENDING':
        return styles.statusPending;
      case 'PROCESSING':
        return styles.statusProcessing;
      case 'COMPLETED':
        return styles.statusCompleted;
      case 'FAILED':
        return styles.statusFailed;
      default:
        return styles.statusDefault;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0sec';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}sec`;
    if (secs === 0) return `${mins}min`;
    return `${mins}min i ${secs}sec`;
  };

  // Don't show queue status for non-processing sessions (unless compact mode)
  if (!compact && session?.status !== 'PROCESSING' && session?.status !== 'FAILED') {
    return null;
  }

  return (
    <div className={styles.container}>
      {/* Main Status Grid */}
      <div className={styles.statusGrid}>
        {/* Session Status */}
        <div className={styles.statusCard}>
          <div className={styles.cardLabel}>Status</div>
          <div className={styles.cardValue}>
            <span className={`${styles.badge} ${getStatusBadgeClass(session?.status)}`}>
              {session?.status || 'UNKNOWN'}
            </span>
          </div>
        </div>

        {/* Queue Position */}
        {(session?.queue_position !== null && session?.queue_position !== undefined) && (
          <div className={styles.statusCard}>
            <div className={styles.cardLabel}>Queue Position</div>
            <div className={styles.cardValue}>
              <span className={styles.highlight}>#{session.queue_position}</span>
            </div>
          </div>
        )}

        {/* Estimated Wait Time */}
        {session?.estimated_wait_time && (
          <div className={styles.statusCard}>
            <div className={styles.cardLabel}>Estimated Wait</div>
            <div className={styles.cardValue}>
              <span className={styles.highlight}>{formatTime(session.estimated_wait_time)}</span>
            </div>
          </div>
        )}

        {/* Processing Time */}
        {session?.status === 'PROCESSING' && elapsedTime && (
          <div className={styles.statusCard}>
            <div className={styles.cardLabel}>Processing For</div>
            <div className={styles.cardValue}>
              <div className={styles.processingTime}>
                <ArrowPathIcon className={styles.spinningIcon} />
                <span>{elapsedTime}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {session?.error_message && (
          <div className={`${styles.statusCard} ${styles.errorCard}`}>
            <div className={styles.cardLabel}>Error</div>
            <div className={styles.cardValue}>
              <span className={styles.errorText}>
                {session.error_message.substring(0, 100)}
                {session.error_message.length > 100 && '...'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Technical Details (Expandable) */}
      {!compact && (session?.celery_task_id || session?.queue_task_id) && (
        <div className={styles.technicalDetailsContainer}>
          <button 
            className={styles.technicalDetailsToggle}
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
          >
            {showTechnicalDetails ? (
              <ChevronUpIcon className={styles.toggleIcon} />
            ) : (
              <ChevronDownIcon className={styles.toggleIcon} />
            )}
            <span>Technical Details</span>
          </button>

          {showTechnicalDetails && (
            <div className={styles.technicalDetails}>
              {session.celery_task_id && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Celery Task ID:</span>
                  <span className={styles.detailValue}>{session.celery_task_id}</span>
                </div>
              )}
              {session.queue_task_id && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Queue Task ID:</span>
                  <span className={styles.detailValue}>{session.queue_task_id}</span>
                </div>
              )}
              {session.transcript_provider && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Provider:</span>
                  <span className={styles.detailValue}>{session.transcript_provider}</span>
                </div>
              )}
              {session.processing_started_at && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Started At:</span>
                  <span className={styles.detailValue}>
                    {new Date(session.processing_started_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionQueueStatus;

